import { useCallback, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { DialogOverlay, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@components/ui/dialog";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import { Button } from "@components/ui/button";
import { Loader2 } from "lucide-react";
import { useYousignIframe } from "../hooks/use-yousign-iframe";

const CONTAINER_ID = "yousign-contract-iframe";

interface Props {
  isOpen: boolean;
  signatureLink: string | null;
  onSignatureDone: () => void;
  onClose: () => void;
  onDeclined: () => void;
  onError: (error: unknown) => void;
}

export const ContractSignatureDialog = ({ isOpen, signatureLink, onSignatureDone, onClose, onDeclined, onError }: Props) => {
  const [signed, setSigned] = useState(false);

  const handleSignatureDone = useCallback(() => {
    setSigned(true);
    onSignatureDone();
  }, [onSignatureDone]);

  const handleClose = useCallback(() => {
    setSigned(false);
    onClose();
  }, [onClose]);

  useYousignIframe({
    signatureLink: isOpen ? signatureLink : null,
    containerId: CONTAINER_ID,
    onSignatureDone: handleSignatureDone,
    onDeclined,
    onError,
  });

  return (
    <DialogPrimitive.Root open={isOpen}>
      <DialogPrimitive.Portal>
        <DialogOverlay>
          <DialogPrimitive.Content
            className={cn(
              "relative z-50 grid w-full max-w-4xl gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg",
            )}
            onPointerDownOutside={e => e.preventDefault()}
            onEscapeKeyDown={e => e.preventDefault()}
            onInteractOutside={e => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle>Signature du contrat de sous-traitance</DialogTitle>
              <DialogDescription>Veuillez signer le contrat ci-dessous pour continuer.</DialogDescription>
            </DialogHeader>
            {!signatureLink && (
              <div className="flex h-[800px] w-full flex-col items-center justify-center gap-3">
                <Loader2 className="size-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Génération du contrat en cours…</p>
              </div>
            )}
            <div id={CONTAINER_ID} className={cn("h-[800px] w-full [&>iframe]:size-full", !signatureLink && "hidden")} />
            {signed && (
              <DialogFooter>
                <Button onClick={handleClose}>Fermer</Button>
              </DialogFooter>
            )}
          </DialogPrimitive.Content>
        </DialogOverlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
